import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";

const ease = [0.22, 1, 0.36, 1];

const clients = [
  "Microsoft", "Accenture", "Android", "BEL", "Reebok", "BIBA", "D'Decor",
];

export default function Clients() {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const labelRef = useRef(null);
  const headingRef = useRef(null);
  const logosRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!sectionRef.current) return;

    // Pre-hide content
    [labelRef, headingRef, logosRef].forEach((r) => {
      if (r.current) {
        r.current.style.opacity = "0";
        r.current.style.transform = "translateY(30px)";
      }
    });

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);

          // Step 1: Video fades in
          if (videoRef.current) videoRef.current.style.opacity = "0.2";

          // Step 2: Label
          setTimeout(() => {
            if (labelRef.current) {
              labelRef.current.style.transition = "opacity 1s cubic-bezier(0.22,1,0.36,1), transform 1s cubic-bezier(0.22,1,0.36,1)";
              labelRef.current.style.opacity = "1";
              labelRef.current.style.transform = "translateY(0)";
            }
          }, 200);

          // Step 3: Heading
          setTimeout(() => {
            if (headingRef.current) {
              headingRef.current.style.transition = "opacity 1s cubic-bezier(0.22,1,0.36,1), transform 1s cubic-bezier(0.22,1,0.36,1)";
              headingRef.current.style.opacity = "1";
              headingRef.current.style.transform = "translateY(0)";
            }
          }, 350);

          // Step 4: Client logos
          setTimeout(() => {
            if (logosRef.current) {
              logosRef.current.style.transition = "opacity 1.2s cubic-bezier(0.22,1,0.36,1), transform 1.2s cubic-bezier(0.22,1,0.36,1)";
              logosRef.current.style.opacity = "1";
              logosRef.current.style.transform = "translateY(0)";
            }
          }, 550);

          observer.disconnect();
        }
      },
      { rootMargin: "-40px 0px" }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="clients-section"
      className="relative py-32 md:py-48 overflow-hidden"
    >
      {/* Background Video */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ opacity: 0, mixBlendMode: "lighten", transition: "opacity 1.5s cubic-bezier(0.22,1,0.36,1)" }}
      >
        <source src="https://customer-assets.emergentagent.com/job_deva-command-nexus/artifacts/xu67d7jb_grok_video_2026-04-18-02-26-16.mp4" type="video/mp4" />
      </video>

      {/* Dark overlays to hide any watermark */}
      <div className="absolute bottom-0 left-0 right-0 h-[20%] bg-gradient-to-t from-[#05070A] to-transparent pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-[10%] bg-gradient-to-b from-[#05070A] to-transparent pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
        <p ref={labelRef} className="sys-label mb-10 md:mb-14">
          SYS.NETWORK //
        </p>

        <h2
          ref={headingRef}
          className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extralight tracking-tight text-white/90 mb-16 md:mb-20 pop-click"
        >
          Built by a Team That Has{" "}
          <span className="bg-gradient-to-r from-[#00E5FF] via-[#7A5CFF] to-[#FF2D9A] bg-clip-text text-transparent">
            Worked With
          </span>
        </h2>

        <div
          ref={logosRef}
          className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 sm:gap-x-10 md:gap-x-14"
        >
          {clients.map((name, i) => (
            <span
              key={name}
              className="font-heading text-lg sm:text-xl md:text-2xl font-extralight tracking-wide text-white/40 hover:text-white/80 transition-colors duration-500"
            >
              {name}
              {i < clients.length - 1 && (
                <span className="ml-6 sm:ml-10 md:ml-14 text-white/10">·</span>
              )}
            </span>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 2.2 }}
          className="mt-10 font-mono text-[10px] tracking-[0.25em] uppercase text-white/25"
        >
          and many more
        </motion.p>
      </div>
    </section>
  );
}
